import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";
import {
  GetFinancialYearById,
  UpdateFinancialYear
} from "../../../api/utility/financialYearAPI";

// Validation schema using Yup
const schema = yup.object().shape({
  name: yup
    .string()
    .required("Financial Year Name is required (e.g., 2025-2026)"),
  startDate: yup
    .date()
    .required("Start Date is required")
    .typeError("Invalid Date"),
  endDate: yup
    .date()
    .required("End Date is required")
    .typeError("Invalid Date")
    .min(yup.ref("startDate"), "End date must be after start date"),
  isActive: yup.boolean().default(true)
});

const EditFinancialYear = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isActive: true,
      name: ""
    }
  });

  const fetchData = async () => {
    try {
      const response = await GetFinancialYearById(id); // Make sure login function returns token
      if (response.success) {
        setValue("name", response.data.name);
        setValue("startDate", response.data.startDate);
        setValue("endDate", response.data.endDate);
        setValue("isActive", response.data.isActive);
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(
    () => {
      if (!id) return;
      // Fetch existing data
      const loadAllData = async () => {
        try {
          // ✅ CALL AFTER ALL ABOVE ARE DONE
          fetchData();
        } catch (error) {
          console.error("Error loading master data:", error);
        }
      };
      loadAllData();
    },
    [id]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdateFinancialYear(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  const yesNo = ["Yes", "No"];

  return (
    <Fragment>
      <Breadcrumb parent="Financial Years" title="Edit Financial Year" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit Financial Year</h5>
            <Link to={`/utility/financial-year/list`}>
              <button className="btn btn-primary" id="list">
                List Financial Years
              </button>
              <UncontrolledTooltip target="list">
                List Financial Years
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
            ref={formRef}
            className="form theme-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="card-body row">
              {/* Name */}
              <div className="col-md-4">
                <label className="form-label">Year Name (e.g. 2025-2026)</label>
                <input
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  {...register("name")}
                  placeholder="2025-2026"
                />
                {errors.name &&
                  <p className="text-danger">
                    {errors.name.message}
                  </p>}
              </div>

              {/* Start Date */}
              <div className="col-md-4">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className={`form-control ${errors.startDate
                    ? "is-invalid"
                    : ""}`}
                  {...register("startDate")}
                />
                {errors.startDate &&
                  <p className="text-danger">
                    {errors.startDate.message}
                  </p>}
              </div>

              {/* End Date */}
              <div className="col-md-4">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className={`form-control ${errors.endDate
                    ? "is-invalid"
                    : ""}`}
                  {...register("endDate")}
                />
                {errors.endDate &&
                  <p className="text-danger">
                    {errors.endDate.message}
                  </p>}
              </div>

              {/* Is Active */}
              <div className="col-md-4 mt-4">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    {...register("isActive")}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Is Active Year?
                  </label>
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button type="submit" className="btn btn-primary">
                Save Financial Year
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditFinancialYear;
